// Запросы целей: /api/goals. Типы зеркалят app/domain/schemas/goals.py.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type GoalKind =
  | 'weight'
  | 'steps'
  | 'sleep'
  | 'workouts_per_week'
  | 'exercise_weight'
  | 'body_fat_pct'
  | 'fat_mass_kg'
export type GoalStatus = 'active' | 'achieved' | 'archived'

/** Дата достижения при текущем темпе; `eta` пуст, когда оценить нельзя (причина в `message_ru`). */
export type GoalForecast = { rate_per_week: number; eta: string | null; message_ru: string }

export type GoalProgress = {
  current: number | null
  percent: number | null
  reached: boolean
  on_track: boolean | null
  days_left: number | null
  forecast?: GoalForecast | null
}

export type Goal = {
  id: string
  kind: GoalKind
  title: string
  unit: string
  exercise_id: string | null
  exercise_name: string | null
  start_value: number | null
  target_value: number
  target_date: string | null
  status: GoalStatus
  achieved_at: string | null
  progress: GoalProgress
}

export type GoalInput = {
  kind: GoalKind
  target_value: number
  start_value: number | null
  target_date: string | null
  exercise_id: string | null
}

export const goalKeys = {
  all: ['goals'] as const,
  list: (status: string) => ['goals', status] as const,
}

export const goalsApi = {
  list: (status: 'active' | 'closed') => api.get<Goal[]>('/goals', { query: { status } }),
  create: (data: GoalInput) => api.post<Goal>('/goals', data),
  setStatus: (id: string, status: GoalStatus) => api.put<Goal>(`/goals/${id}/status`, { status }),
  remove: (id: string) => api.delete<{ change_id: string }>(`/goals/${id}`),
  undo: (changeId: string) => api.post(`/changes/${changeId}/undo`),
}

export const useGoals = (status: 'active' | 'closed') =>
  useQuery({ queryKey: goalKeys.list(status), queryFn: () => goalsApi.list(status) })

/** Любая запись по целям сбрасывает цели и «Сегодня» (там тоже есть блок целей и чек-лист). */
export function useInvalidateGoals() {
  const client = useQueryClient()
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: goalKeys.all }),
      client.invalidateQueries({ queryKey: ['today'] }),
      client.invalidateQueries({ queryKey: ['changes'] }),
    ])
}

export function useGoalMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const invalidate = useInvalidateGoals()
  return useMutation({ mutationFn: fn, onSuccess: () => invalidate() })
}
